import React, { useCallback, useEffect, useState } from 'react';
import {
    Layout, Card, Row, Col, Typography, Space, Button, Input,
    Avatar, Divider, message, Spin, Select, Progress, Tag, DatePicker
} from 'antd';
import {
    UserOutlined, EditOutlined, SaveOutlined,
    CloseOutlined, CrownOutlined
} from '@ant-design/icons';
import { SecureGet, SecurePatch } from '../../services/axiosCall';
import apis from '../../services/Apis';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';

const { Content } = Layout;
const { Title, Text } = Typography;

// Role display config — extend as new roles are added
const ROLE_CONFIG = {
    hod: { label: 'Head of Department', color: '#722ed1' },
    dean: { label: 'Dean', color: '#eb2f96' },
    vc: { label: 'Vice Chancellor', color: '#f5222d' },
    faculty: { label: 'Faculty', color: '#1890ff' },
};

// Workload metrics config — order controls display order
const WORKLOAD_METRICS = [
    { key: 'teaching_workload_percentage', label: 'Teaching', color: '#1890ff' },
    { key: 'research_workload_percentage', label: 'Research', color: '#722ed1' },
    { key: 'service_workload_percentage', label: 'Service', color: '#13c2c2' },
];

// Static info fields — order controls display order
const INFO_FIELDS = [
    { key: 'department', label: 'Department' },
    { key: 'school', label: 'School', suffixKey: 'school_short_name' },
    { key: 'email', label: 'Email' },
];

const Profile = ({ userInfo, onUpdate }) => {
    const _static = useSelector((state) => state.static);
    const { staticData } = _static;
    const { designation } = staticData;

    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [profileData, setProfileData] = useState(null);
    const [editedData, setEditedData] = useState({
        name: '',
        designation: '',
        faculty_joining_date: null
    });

    const getProfile = useCallback(async () => {
        if (!userInfo?.id) return;
        setLoading(true);
        try {
            const response = await SecureGet({
                url: `${apis.USER_PROFILE}/${userInfo.id}`,
            });
            if (response?.data) {
                setProfileData(response.data);

                const rawDesignation = response.data.designation;
                const matched = designation?.find(
                    (d) => d.value === rawDesignation || d.label === rawDesignation
                );

                setEditedData({
                    name: response.data.name || '',
                    designation: matched?.value ?? rawDesignation ?? '',
                    faculty_joining_date: response.data.faculty_joining_date ? dayjs(response.data.faculty_joining_date) : null
                });
            }
        } catch (error) {
            console.error('Error fetching profile:', error);
            message.error('Failed to load profile data');
        } finally {
            setLoading(false);
        }
    }, [userInfo?.id, designation]);

    const updateProfile = async (updatedData) => {
        setLoading(true);
        try {
            const response = await SecurePatch({
                url: `${apis.USER_PROFILE}/${userInfo.id}`,
                data: updatedData
            });
            if (response?.data) {
                setProfileData(response.data);
                message.success("Profile updated successfully!");
                getProfile();
                if (onUpdate) onUpdate(response.data);
                return true;
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            message.error(error?.response?.data?.message || 'Failed to update profile');
            return false;
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = () => {
        setIsEditing(true);
        const rawDesignation = profileData?.designation;
        const matched = designation?.find(
            (d) => d.value === rawDesignation || d.label === rawDesignation
        );
        setEditedData({
            name: profileData?.name || '',
            designation: matched?.value ?? rawDesignation ?? '',
            faculty_joining_date: profileData?.faculty_joining_date ? dayjs(profileData.faculty_joining_date) : null
        });
    };

    const handleSave = async () => {
        if (!editedData.name.trim()) {
            message.error("Name cannot be empty");
            return;
        }

        const resolvedDesignation = designation?.find(
            (d) => d.value === editedData.designation
        )?.value ?? profileData?.designation;

        if (!resolvedDesignation) {
            message.error("Designation cannot be resolved");
            return;
        }

        const success = await updateProfile({
            name: editedData.name,
            designation: resolvedDesignation,
            faculty_joining_date: editedData.faculty_joining_date?.format('YYYY-MM-DD') ?? null
        });

        if (success) setIsEditing(false);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditedData({
            name: profileData?.name || '',
            designation: profileData?.designation || '',
            faculty_joining_date: profileData?.faculty_joining_date ? dayjs(profileData.faculty_joining_date) : null
        });
    };

    const handleChange = (field, value) => {
        setEditedData(prev => ({ ...prev, [field]: value }));
    };

    const getDesignationLabel = (value) => {
        const found = designation?.find((d) => d.value === value);
        return found ? found.label : value;
    };

    useEffect(() => {
        getProfile();
    }, [getProfile]);

    if (loading && !profileData) {
        return (
            <Content style={{ padding: '24px', minHeight: '40vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Spin size="large" tip="Loading profile..." />
            </Content>
        );
    }

    const displayData = profileData;

    if (!displayData) {
        return (
            <Content style={{ padding: '24px' }}>
                <Card style={{ textAlign: 'center' }}>
                    <Text>No profile data available</Text>
                </Card>
            </Content>
        );
    }

    const roleInfo = ROLE_CONFIG[displayData?.role] || { label: displayData?.role, color: '#8c8c8c' };

    return (
        <Card
            style={{
                borderRadius: '8px',
                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.06)',
                overflow: 'hidden'
            }}
            styles={{ body: { padding: 0 } }}
        >
            {/* Header */}
            <div style={{
                padding: '24px 24px 20px',
                textAlign: 'center',
                borderBottom: '1px solid',
                borderColor: 'var(--ant-color-border)'
            }}>
                <Avatar
                    size={72}
                    icon={<UserOutlined />}
                    style={{ backgroundColor: '#1890ff', color: '#fff', marginBottom: 12 }}
                />
                <Title level={4} style={{ marginBottom: 2 }}>
                    {displayData.name}
                </Title>
                <Text style={{ fontSize: 14, color: '#1890ff', fontWeight: 500 }}>
                    {getDesignationLabel(displayData?.designation)}
                </Text>
                <div style={{ marginTop: 8 }}>
                    <Tag
                        icon={<CrownOutlined />}
                        color={roleInfo.color}
                        style={{
                            borderRadius: 20,
                            padding: '2px 10px',
                            fontSize: 12,
                            fontWeight: 500,
                            border: 'none'
                        }}
                    >
                        {roleInfo.label}
                    </Tag>
                </div>
            </div>

            {/* Main Content */}
            <div style={{ padding: '20px 24px' }}>
                {/* Editable fields */}
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                        <Text type="secondary" style={{ fontSize: 12 }}>Full Name</Text>
                        {isEditing ? (
                            <Input
                                value={editedData.name}
                                onChange={(e) => handleChange('name', e.target.value)}
                                style={{ marginTop: 4 }}
                            />
                        ) : (
                            <div style={{ marginTop: 4, fontSize: 14, fontWeight: 500 }}>
                                {displayData?.name}
                            </div>
                        )}
                    </Col>
                    <Col xs={24} sm={12}>
                        <Text type="secondary" style={{ fontSize: 12 }}>Designation</Text>
                        {isEditing ? (
                            <Select
                                value={editedData.designation}
                                onChange={(value) => handleChange('designation', value)}
                                style={{ marginTop: 4, width: '100%' }}
                                options={designation?.map((d) => ({
                                    label: d.label,
                                    value: d.value,
                                }))}
                                placeholder="Select designation"
                            />
                        ) : (
                            <div style={{ marginTop: 4, fontSize: 14, fontWeight: 500, color: '#1890ff' }}>
                                {getDesignationLabel(displayData?.designation)}
                            </div>
                        )}
                    </Col>
                    <Col xs={24} sm={12}>
                        <Text type="secondary" style={{ fontSize: 12 }}>Faculty Joining Date</Text>
                        {isEditing ? (
                            <DatePicker
                                aria-label="Faculty Joining Date"
                                value={editedData.faculty_joining_date}
                                onChange={(value) => handleChange('faculty_joining_date', value)}
                                format="YYYY-MM-DD"
                                style={{ marginTop: 4, width: '100%' }}
                            />
                        ) : (
                            <div style={{ marginTop: 4, fontSize: 14 }}>
                                {displayData.faculty_joining_date || '—'}
                            </div>
                        )}
                    </Col>
                </Row>

                {/* Static info grid */}
                <Row gutter={[16, 14]} style={{ marginTop: 16 }}>
                    {INFO_FIELDS.map(({ key, label, suffixKey }) => (
                        <Col xs={24} sm={8} key={key}>
                            <Text type="secondary" style={{ fontSize: 12 }}>{label}</Text>
                            <div style={{ marginTop: 4, fontSize: 14 }}>
                                {displayData?.[key]}
                                {suffixKey && displayData?.[suffixKey] && ` (${displayData[suffixKey]})`}
                            </div>
                        </Col>
                    ))}
                </Row>

                {/* Workload Distribution */}
                <Divider style={{ margin: '20px 0 16px' }} />
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
                    Workload Distribution
                </Text>
                <Row gutter={[12, 12]}>
                    {WORKLOAD_METRICS.map(({ key, label, color }) => {
                        const value = displayData?.[key] ?? 0;
                        return (
                            <Col xs={8} key={key}>
                                <div
                                    style={{
                                        textAlign: 'center',
                                        padding: '12px 8px',
                                        borderRadius: 10,
                                        background: `${color}0D`,
                                        border: `1px solid ${color}26`,
                                    }}
                                >
                                    <Progress
                                        type="circle"
                                        percent={value}
                                        size={60}
                                        strokeColor={color}
                                        strokeWidth={9}
                                        format={(percent) => (
                                            <span style={{ fontSize: 13, fontWeight: 600, color }}>
                                                {percent}%
                                            </span>
                                        )}
                                    />
                                    <div style={{ marginTop: 8, fontSize: 12, fontWeight: 500, color: '#595959' }}>
                                        {label}
                                    </div>
                                </div>
                            </Col>
                        );
                    })}
                </Row>

                <Divider style={{ margin: '20px 0 16px' }} />

                {/* Action Buttons */}
                <div style={{ textAlign: 'center' }}>
                    {!isEditing ? (
                        <Button
                            type="primary"
                            icon={<EditOutlined />}
                            onClick={handleEdit}
                            style={{ padding: '0 28px' }}
                            loading={loading}
                        >
                            Edit Profile
                        </Button>
                    ) : (
                        <Space size="small">
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                onClick={handleSave}
                                style={{ padding: '0 24px' }}
                                loading={loading}
                            >
                                Save
                            </Button>
                            <Button
                                danger
                                icon={<CloseOutlined />}
                                onClick={handleCancel}
                                style={{ padding: '0 24px' }}
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                        </Space>
                    )}
                </div>
            </div>
        </Card>
    );
};

export default Profile;